import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CreditCard, ShieldCheck, AlertTriangle, CheckCircle2, ExternalLink } from "lucide-react";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
};

const CancelButton = ({ until, onConfirm, busy }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <button data-testid="company-cancel-subscription" className="btn-secondary text-xs py-2 text-[#B91C1C] border-[#B91C1C]/30">Cancel subscription</button>
    </AlertDialogTrigger>
    <AlertDialogContent data-testid="company-cancel-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Cancel your company subscription?</AlertDialogTitle>
        <AlertDialogDescription>All of your collectors keep receiving customers until <span className="font-medium">{fmtDate(until)}</span>. After that none of them will be connected to customers and you won't be charged again.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="company-cancel-dismiss">Keep subscription</AlertDialogCancel>
        <AlertDialogAction data-testid="company-cancel-confirm" onClick={onConfirm} disabled={busy} className="bg-[#B91C1C] hover:bg-[#991818]">Yes, cancel</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const CompanyBillingCard = ({ company, billing, reload }) => {
  const [busy, setBusy] = useState(false);
  const { subscription_active: active, subscription_cancelled: cancelled, subscription_until: until } = company;

  const call = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const subscribe = () => call(async () => {
    const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, plan: "monthly" });
    window.location.href = data.url;
  });
  const portal = () => call(async () => {
    const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
    window.location.href = data.url;
  });
  const post = (path, msg) => call(async () => { await api.post(path); toast.success(msg); await reload(); });

  return (
    <div className="card-soft" data-testid="company-billing-card">
      <h2 className="font-heading text-xl text-[#1E3F33] mb-3 flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#2D6A4F]"/> Subscription</h2>
      <div className="rounded-xl bg-[#F0EFEA] p-4" data-testid="company-billing-summary">
        <div className="text-xs uppercase tracking-wider text-[#8A9A86]">Monthly total</div>
        <div className="font-heading text-3xl text-[#1E3F33] mt-1" data-testid="company-monthly-total">${billing.monthly_total.toFixed(2)}<span className="text-base text-[#8A9A86]">/mo</span></div>
        <div className="text-xs text-[#5C6B64] mt-1" data-testid="company-seat-math">{billing.seats} collector{billing.seats === 1 ? "" : "s"} × ${billing.price_per_seat.toFixed(2)}</div>
      </div>
      {active && !cancelled && (
        <div className="mt-4" data-testid="company-sub-active">
          <div className="flex items-center gap-2 text-[#2D6A4F]"><ShieldCheck className="w-4 h-4"/><span className="font-medium text-sm">Active — renews {fmtDate(until)}</span></div>
          <p className="text-xs text-[#5C6B64] mt-2">Adding or removing collectors updates your bill automatically — new collectors are charged a prorated amount now and the full rate from your next renewal.</p>
          <div className="flex flex-wrap gap-2 mt-4">
            {company.has_billing_account && <button onClick={portal} disabled={busy} data-testid="company-billing-portal" className="btn-secondary text-xs py-2 inline-flex items-center gap-1.5"><ExternalLink className="w-3.5 h-3.5"/> Manage billing</button>}
            <CancelButton until={until} busy={busy} onConfirm={() => post("/payments/cancel-subscription", "Subscription cancelled — it won't renew.")} />
          </div>
        </div>
      )}
      {active && cancelled && (
        <div className="mt-4" data-testid="company-sub-cancelled">
          <div className="flex items-center gap-2 text-[#B45309]"><AlertTriangle className="w-4 h-4"/><span className="font-medium text-sm">Cancelled — access until {fmtDate(until)}</span></div>
          <button onClick={() => post("/payments/reactivate-subscription", "Subscription resumed.")} disabled={busy} data-testid="company-reactivate-subscription" className="btn-primary mt-4 text-xs py-2 inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5"/> Resume subscription</button>
        </div>
      )}
      {!active && (
        <div className="mt-4" data-testid="company-sub-inactive">
          <div className="flex items-center gap-2 text-[#8A9A86]"><AlertTriangle className="w-4 h-4"/><span className="font-medium text-sm">No active subscription</span></div>
          <p className="text-xs text-[#5C6B64] mt-2">{billing.seats ? "Subscribe so your collectors start receiving customer leads." : "Add at least one collector, then subscribe."}</p>
          <button onClick={subscribe} disabled={busy || !billing.seats} data-testid="company-subscribe-button" className="btn-accent mt-4 text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
            <CreditCard className="w-4 h-4"/> {busy ? "Redirecting…" : `Subscribe — $${billing.monthly_total.toFixed(2)}/mo`}
          </button>
        </div>
      )}
    </div>
  );
};
